import React, { useState } from 'react';
import { resetPassword } from '../../../../api/authApi';
import { useAuth } from '../../../../AuthContext';
import { useProfileSheet } from '../../../../contexts/ProfileSheetContext';
import { validatePassword } from '../../../../utils/passwordValidation';
import SheetField from '../shared/SheetField';
import SheetMessage from '../shared/SheetMessage';
import PasswordStrengthMeter from '../shared/PasswordStrengthMeter';

interface CreatePasswordViewProps {
  /** Hands the one-shot success banner to the login view. */
  onComplete: (notice: string) => void;
}

/**
 * First-login password creation — the /welcome?token= emailed deep link.
 * Admins create the account and send the welcome email; the token is a 24h
 * PasswordResetToken and the backend reuses the reset-password endpoint.
 */
const CreatePasswordView: React.FC<CreatePasswordViewProps> = ({ onComplete }) => {
  const { token, setView } = useProfileSheet();
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  // Derived, not captured at mount: the deep-link open can deliver the token
  // a render after the view mounts — deriving lets the view heal.
  const [apiTokenError, setApiTokenError] = useState(false);
  const tokenError = apiTokenError || !token;
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: { [k: string]: string } = {};
    const pwErr = validatePassword(password);
    if (pwErr) errs.password = pwErr;
    if (confirm !== password) errs.confirm = 'Passwords do not match';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0 || !token) return;

    setError(null);
    setSubmitting(true);
    try {
      const result = await resetPassword(token, password);
      if (result.username) {
        // The token proved control of the email — sign them straight in with
        // the password they just chose (auth-success effect handles landing).
        try {
          await login(result.username, password);
          return;
        } catch {
          // Fall through to the manual sign-in path.
        }
      }
      onComplete('Password set — sign in with your new password.');
      setView('login');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      if (/token|expired|invalid/i.test(message)) {
        setApiTokenError(true);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SheetMessage kind="err">
          This welcome link is invalid or has expired. Please contact your
          administrator for a new invitation.
        </SheetMessage>
        <div className="tend-links-row">
          <button type="button" className="tend-link" onClick={() => setView('login')}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ color: 'var(--text-2)', fontSize: 13.5, lineHeight: 1.55, margin: 0 }}>
        Welcome! Choose a password to finish setting up your account.
      </p>
      {error && <SheetMessage kind="err">{error}</SheetMessage>}
      <SheetField
        label="Create password"
        value={password}
        onChange={setPassword}
        type="password"
        name="new-password"
        autoComplete="new-password"
        error={fieldErrors.password}
        disabled={submitting}
        autoFocus
      />
      <PasswordStrengthMeter password={password} />
      <SheetField
        label="Confirm password"
        value={confirm}
        onChange={setConfirm}
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        error={fieldErrors.confirm}
        disabled={submitting}
      />
      <button
        type="submit"
        className="tend-btn tend-btn--cta tend-btn--block"
        disabled={submitting}
        style={{ marginTop: 4 }}
      >
        {submitting ? 'Setting password…' : 'Set Password'}
      </button>
    </form>
  );
};

export default CreatePasswordView;
